/**
 * React Query hooks for blocked users
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';

export interface BlockedUser {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  blocked_at: string;
}

export interface BlockedUsersResponse {
  blocked_users: BlockedUser[];
  page: number;
  total_pages: number;
  total_results: number;
}

const PAGE_SIZE = 20;

/**
 * Lightweight count-only query for blocked users.
 * Fires on Privacy tab mount. Fetches page=1&limit=1 to get total_results.
 * Key: ['blocked', 'count', userId]
 */
export function useBlockedUsersCount() {
  const { user, session } = useAuth();
  const token = session?.access_token;

  return useQuery({
    queryKey: queryKeys.blocked.count(user?.id ?? ''),
    enabled: !!user && !!token,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({ page: '1', limit: '1' });
      const res = await apiFetch<BlockedUsersResponse>(`/user/blocked?${params}`, { token, signal });
      return res.total_results;
    },
    refetchOnWindowFocus: true,
  });
}

/**
 * Infinite query for blocked users — modal only.
 * Inherits global staleTime (60s) and refetchOnMount (false).
 */
export function useBlockedUsersInfinite() {
  const { user, session } = useAuth();
  const token = session?.access_token;

  return useInfiniteQuery({
    queryKey: queryKeys.blocked.list(user?.id ?? ''),
    enabled: !!user && !!token,
    queryFn: async ({ pageParam = 1, signal }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        limit: String(PAGE_SIZE),
      });

      return apiFetch<BlockedUsersResponse>(`/user/blocked?${params}`, { token, signal });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.total_pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    refetchOnWindowFocus: true,
  });
}
